import React, { useEffect, useMemo, useState } from 'react';
import styles from './IncomeQualification.module.scss';
import { useTranslation } from 'react-i18next';
import InsightAI from '../../../common/InsightAI/InsightAI';
import { useAppSelector } from '../../../../redux/hooks';
import PersonalPvTile, { PersonalPvDetail } from './KPI/PersonalPv/PersonalPvTile';
import GroupPvTile, { GroupPvDetail } from './KPI/GroupPv/GroupPvTile';
import TotalIncomeTile, { TotalIncomeDetail } from './KPI/TotalIncome/TotalIncomeTile';
import CorePlusIncomeTile, { CorePlusIncomeDetail } from './KPI/CorePlusIncome/CorePlusIncomeTile';
import CorePlanIncomeTile, { CorePlanIncomeDetail } from './KPI/CorePlanIncome/CorePlanIncomeTile';
import RetailProfitTile, { RetailProfitDetail } from './KPI/RetailProfit/RetailProfitTile';
import { useGetBusinessPerformanceSummaryQuery } from '../../../../redux/services/businessPerformaceAPI';
import { isAISummariesAccessible, useProfileParams } from '../../../../utils/common';
import { isEmpty } from 'lodash';
import { useGetKpiSettingsQuery } from '../../../../redux/services/kpiSettingsByTypeAPI';
import InteractiveTileGrid, { TilePair } from '../common/InteractiveTileGrid/InteractiveTileGrid';
import ErrorInPageScreen from '../../../common/ErrorInPageScreen/ErrorInPageScreen';
import { isKpiInConfig } from '../../../../utils/agcUtil';

const IncomeQualification: React.FC = () => {
  const { t } = useTranslation();
  const isExpanded = useAppSelector((state: any) => state.sideNavExpansion?.isExpanded);
  const { affiliateCode, aboNumber } = useProfileParams();

  const [totalIncomeKPICodeList, setTotalIncomeKPICodeList] = useState<any>([]);

  const {
    data: totalIncomeKpisResponse,
    isFetching: isFetchingKpiSettings,
    isError: isKpiSettingsError,
    refetch: refetchKpiSettings,
  } = useGetKpiSettingsQuery(
    {
      type: 'business-performance-income-qualification',
      affiliateCode,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    const response = totalIncomeKpisResponse?.body || [];
    const kpiCodes = response.map((item: { code: string }) => ({
      code: item?.code,
    }));

    setTotalIncomeKPICodeList(kpiCodes);
  }, [totalIncomeKpisResponse]);

  const {
    data: summaryResp,
    isFetching: isSummaryFetching,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetBusinessPerformanceSummaryQuery(
    {
      affiliateCode,
      aboNumber,
      payload: { kpis: totalIncomeKPICodeList },
    },
    {
      skip: Number(affiliateCode) === 0 || aboNumber === 0 || isEmpty(totalIncomeKPICodeList),
      refetchOnMountOrArgChange: true,
    }
  );

  const fetchedKpis = useMemo(() => {
    return summaryResp?.body?.kpis ?? [];
  }, [summaryResp]);

  const loading = isSummaryFetching || isFetchingKpiSettings;
  const error = isKpiSettingsError || isSummaryError;

  // Safe retry handler - only refetch queries that have been started
  const handleRetry = () => {
    refetchKpiSettings();
    // Only refetch summary if the query was started (not skipped)
    if (!isEmpty(totalIncomeKPICodeList)) {
      refetchSummary();
    }
  };

  // Extract KPIs from API response
  const personalPvKpi = fetchedKpis.find((k: any) => k?.kpiCode === 'personalPv');
  const groupPvKpi = fetchedKpis.find((k: any) => k?.kpiCode === 'groupPV');
  const totalIncomeKpi = fetchedKpis.find((k: any) => k?.kpiCode === 'totalIncome');
  const corePlusIncomeKpi = fetchedKpis.find((k: any) => k?.kpiCode === 'corePlusIncome');
  const corePlanIncomeKpi = fetchedKpis.find((k: any) => k?.kpiCode === 'corePlanIncome');
  const retailProfitKpi = fetchedKpis.find((k: any) => k?.kpiCode === 'retailProfit');

  // Check if KPI code exists in config
  const configKpis = totalIncomeKpisResponse?.body || [];
  const isInConfig = (code: string) => isKpiInConfig(configKpis, code);

  const allTiles = [
    isInConfig('personalPv') && {
      id: 'personalPv',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <PersonalPvTile summary={personalPvKpi} loading={loading} />
      ),
      kpiDetailTile: <PersonalPvDetail />,
    },
    isInConfig('groupPV') && {
      id: 'groupPv',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <GroupPvTile summary={groupPvKpi} loading={loading} />
      ),
      kpiDetailTile: <GroupPvDetail />,
    },
    isInConfig('totalIncome') && {
      id: 'totalIncome',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <TotalIncomeTile summary={totalIncomeKpi} loading={loading} />
      ),
      kpiDetailTile: <TotalIncomeDetail />,
    },
    isInConfig('corePlusIncome') && {
      id: 'corePlusIncome',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <CorePlusIncomeTile summary={corePlusIncomeKpi} loading={loading} />
      ),
      kpiDetailTile: <CorePlusIncomeDetail />,
    },
    isInConfig('corePlanIncome') && {
      id: 'corePlanIncome',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <CorePlanIncomeTile summary={corePlanIncomeKpi} loading={loading} />
      ),
      kpiDetailTile: <CorePlanIncomeDetail />,
    },
    isInConfig('retailProfit') && {
      id: 'retailProfit',
      isVisible: retailProfitKpi?.kpiValue !== 0,
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <RetailProfitTile summary={retailProfitKpi} loading={loading} />
      ),
      kpiDetailTile: <RetailProfitDetail />,
    },
  ].filter(Boolean) as TilePair[];

  return (
    <div className={styles.incomeQualification_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t('income_qualification')}</div>
      </div>

      {isAISummariesAccessible() && (
        <InsightAI
          title={'ai_summary_income_qualification'}
          content={''}
          className={styles.insight_ai}
          showDropdown={true}
          isDropdownEnabled={true}
        />
      )}

      <div className={styles.tiles_grid_container}>
        <InteractiveTileGrid
          gridColumns={isExpanded ? 3 : 4}
          tiles={allTiles}
        />
      </div>
    </div>
  );
};

export default IncomeQualification;
